import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from './Toast';
import { formatDate } from '../format';
import {
  addGroupMember,
  createGrant,
  createGroup,
  getGroup,
  getGroupDirectory,
  listGroups,
  removeGroupMember,
  revokeGrant,
  updateGroupMemberRole,
} from '../api/groups';
import type {
  AccessPermission,
  CurrentUser,
  Group,
  GroupAccessGrant,
  GroupDetail,
  GroupMember,
  GroupRole,
  GroupSummary,
} from '../api/types';
import { CopyButton } from './CopyButton';
import { FieldLabel } from './FieldLabel';
import { DataGrid } from './DataGrid';
import { ErrorBanner } from './ErrorBanner';
import { UserPicker, type UserSelection } from './UserPicker';

interface GroupsAdminPanelProps {
  me: CurrentUser | null;
  /** Called after any change that may alter the caller's own capabilities, such as a promotion. */
  onPermissionsChanged: () => void;
}

/**
 * Administration group by group: create one, read its members, share its QR codes with another group. The
 * complementary reading - one row per person, with everything they reach - lives in {@link UsersAdminPanel}.
 */
export function GroupsAdminPanel({ me, onPermissionsChanged }: GroupsAdminPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [detail, setDetail] = useState<GroupDetail | null>(null);
  const [directory, setDirectory] = useState<GroupSummary[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');

  const [memberSelection, setMemberSelection] = useState<UserSelection | null>(null);
  const [memberRole, setMemberRole] = useState<GroupRole>('Member');

  const [grantTargetId, setGrantTargetId] = useState('');
  const [grantPermission, setGrantPermission] = useState<AccessPermission>('View');

  const reloadGroups = useCallback(async () => {
    try {
      const loaded = await listGroups();
      setGroups(loaded);
      setSelectedId(
        (current) =>
          // Prefer a group the user administers, but still open something for a plain member: they can read
          // their group, they just cannot change it.
          current || loaded.find((group) => group.myRole === 'GroupAdmin')?.id || loaded[0]?.id || '',
      );
    } catch (caught) {
      setError(caught);
    }
  }, []);

  const reloadDetail = useCallback(async () => {
    if (!selectedId) {
      setDetail(null);
      return;
    }

    try {
      setDetail(await getGroup(selectedId));
      setError(null);
    } catch (caught) {
      setError(caught);
    }
  }, [selectedId]);

  useEffect(() => {
    void reloadGroups();
  }, [reloadGroups]);

  useEffect(() => {
    void reloadDetail();
  }, [reloadDetail]);

  // Only group admins can browse the directory; a 403 here is expected for plain members.
  useEffect(() => {
    if (!groups.some((group) => group.myRole === 'GroupAdmin')) {
      return;
    }

    getGroupDirectory()
      .then(setDirectory)
      .catch(() => setDirectory([]));
  }, [groups]);

  async function run(action: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await action();
      setError(null);
      await reloadGroups();
      await reloadDetail();
      // Promoting or demoting somebody can change the caller's own capabilities.
      onPermissionsChanged();
      toast.success(done);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function handleCreateGroup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!newGroupName.trim()) {
      return;
    }

    await run(async () => {
      const created = await createGroup(newGroupName.trim(), newGroupDescription.trim() || undefined);
      setNewGroupName('');
      setNewGroupDescription('');
      setSelectedId(created.id);
    }, t('toast.groupCreated'));
  }

  async function handleAddMember(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (memberSelection === null) {
      return;
    }

    await run(async () => {
      await addGroupMember(
        selectedId,
        memberSelection.userId,
        memberRole,
        memberSelection.displayName ?? undefined,
      );
      setMemberSelection(null);
      setMemberRole('Member');
    }, t('toast.memberAdded'));
  }

  async function handleCreateGrant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!grantTargetId) {
      return;
    }

    await run(async () => {
      await createGrant(selectedId, grantTargetId, grantPermission);
      setGrantTargetId('');
    }, t('toast.accessGranted'));
  }

  // Read from the API rather than inferred from myRole: a super admin administers groups they are not in.
  const isAdminOfSelected = detail?.group.canAdminister === true;
  const canCreateGroups = me?.canCreateGroups === true;
  const canGrantSuperAdmin = me?.isSuperAdmin === true;
  const grantCandidates = directory.filter((candidate) => candidate.id !== selectedId);

  const assignableRoles: GroupRole[] = canGrantSuperAdmin
    ? ['Member', 'GroupAdmin', 'SuperAdmin']
    : ['Member', 'GroupAdmin'];

  const groupColumns = useMemo<ColDef<Group>[]>(
    () => [
      {
        field: 'name',
        headerName: t('admin.groups.name'),
        flex: 2,
        minWidth: 160,
        cellRenderer: ({ data }: ICellRendererParams<Group>) =>
          data && (
            <span className="d-flex align-items-center gap-2 w-100">
              <span className="text-truncate cell-truncate">{data.name}</span>
              {!data.isMember && (
                <span className="badge badge-neutral flex-shrink-0">
                  <i className="pi pi-share-alt me-1" aria-hidden="true" />
                  {t('admin.groups.sharedWithMe')}
                </span>
              )}
            </span>
          ),
      },
      {
        colId: 'role',
        headerName: t('common.role'),
        flex: 1,
        minWidth: 150,
        valueGetter: ({ data }) => (data?.myRole ? t(`admin.roles.${data.myRole}`) : t('common.none')),
      },
      {
        field: 'memberCount',
        headerName: t('admin.groups.memberCount'),
        type: 'numericColumn',
        width: 110,
      },
      {
        field: 'qrCodeCount',
        headerName: t('admin.groups.qrCodeCount'),
        type: 'numericColumn',
        width: 110,
      },
      {
        colId: 'actions',
        headerName: t('common.actions'),
        sortable: false,
        width: 130,
        cellRenderer: ({ data }: ICellRendererParams<Group>) =>
          data && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setSelectedId(data.id)}
            >
              <i className="pi pi-cog me-1" aria-hidden="true" />
              {t('common.manage')}
            </button>
          ),
      },
    ],
    [t],
  );

  const memberColumns = useMemo<ColDef<GroupMember>[]>(
    () => [
      {
        colId: 'user',
        headerName: t('common.user'),
        flex: 2,
        minWidth: 160,
        valueGetter: ({ data }) => data?.displayName ?? t('common.none'),
      },
      {
        field: 'userId',
        headerName: t('admin.groups.entraObjectId'),
        flex: 2,
        minWidth: 200,
        cellRenderer: ({ data }: ICellRendererParams<GroupMember>) =>
          data && (
            <span className="mono text-truncate cell-truncate d-block" title={data.userId}>
              {data.userId}
            </span>
          ),
      },
      {
        colId: 'role',
        headerName: t('common.role'),
        width: 190,
        sortable: false,
        cellRenderer: ({ data }: ICellRendererParams<GroupMember>) => {
          if (!data) {
            return null;
          }

          if (!isAdminOfSelected) {
            return t(`admin.roles.${data.role}`);
          }

          return (
            <select
              className="form-select form-select-sm"
              aria-label={t('admin.users.roleOf', { user: data.displayName ?? data.userId })}
              value={data.role}
              disabled={busy}
              onChange={(event) =>
                void run(() =>
                  updateGroupMemberRole(selectedId, data.userId, event.target.value as GroupRole),
                  t('toast.roleChanged'),
                )
              }
            >
              {/* A member who already is a super admin keeps the option visible, so their role is not
                  silently misrepresented to an admin who cannot grant it. */}
              {(data.role === 'SuperAdmin' && !canGrantSuperAdmin
                ? [...assignableRoles, 'SuperAdmin' as GroupRole]
                : assignableRoles
              ).map((role) => (
                <option key={role} value={role}>
                  {t(`admin.roles.${role}`)}
                </option>
              ))}
            </select>
          );
        },
      },
      {
        colId: 'actions',
        headerName: t('common.actions'),
        sortable: false,
        width: 130,
        cellRenderer: ({ data }: ICellRendererParams<GroupMember>) =>
          data && isAdminOfSelected ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              disabled={busy}
              onClick={() =>
                void run(() => removeGroupMember(selectedId, data.userId), t('toast.memberRemoved'))
              }
            >
              <i className="pi pi-user-minus me-1" aria-hidden="true" />
              {t('common.remove')}
            </button>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isAdminOfSelected, busy, selectedId, canGrantSuperAdmin],
  );

  /** The two sharing tables differ only in which side of the grant they name, and whether it can be revoked. */
  const grantColumns = useCallback(
    (side: 'given' | 'received'): ColDef<GroupAccessGrant>[] => [
      {
        colId: 'group',
        headerName: t('common.group'),
        flex: 2,
        minWidth: 150,
        valueGetter: ({ data }) =>
          side === 'given' ? data?.grantedToGroupName : data?.ownerGroupName,
      },
      {
        colId: 'permission',
        headerName: t('common.permission'),
        width: 150,
        valueGetter: ({ data }) => (data ? t(`admin.permissions.${data.permission}`) : ''),
      },
      {
        colId: 'since',
        headerName: t('common.since'),
        width: 130,
        valueGetter: ({ data }) => formatDate(data?.createdAt),
      },
      ...(side === 'given'
        ? [
            {
              colId: 'actions',
              headerName: t('common.actions'),
              sortable: false,
              width: 130,
              cellRenderer: ({ data }: ICellRendererParams<GroupAccessGrant>) =>
                data && isAdminOfSelected ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={busy}
                    onClick={() =>
                      void run(() => revokeGrant(selectedId, data.id), t('toast.accessRevoked'))
                    }
                  >
                    <i className="pi pi-ban me-1" aria-hidden="true" />
                    {t('admin.groups.revoke')}
                  </button>
                ) : null,
            } satisfies ColDef<GroupAccessGrant>,
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isAdminOfSelected, busy, selectedId],
  );

  return (
    <>
      <ErrorBanner error={error} />

      {!canCreateGroups && me !== null && (
        <div className="card">
          <h2>
            <i className="pi pi-plus-circle me-2" aria-hidden="true" />
            {t('admin.groups.createHeading')}
          </h2>
          <div className="alert alert-info d-flex gap-2" role="status">
            <i className="pi pi-info-circle mt-1" aria-hidden="true" />
            <span>{t('admin.groups.onlySuperAdmin')}</span>
          </div>
          <p className="mono mb-1">{me.userId}</p>
          <CopyButton value={me.userId} label={t('admin.groups.copyMyObjectId')} />
        </div>
      )}

      {canCreateGroups && (
        <div className="card">
          <h2>
            <i className="pi pi-plus-circle me-2" aria-hidden="true" />
            {t('admin.groups.createHeading')}
          </h2>
          <p className="hint">{t('admin.groups.createHint')}</p>
          <form onSubmit={handleCreateGroup} aria-label={t('admin.groups.createHeading')}>
            <div className="field">
              <FieldLabel htmlFor="new-group-name" icon="pi-tag" required>
                {t('admin.groups.name')}
              </FieldLabel>
              <input
                id="new-group-name"
                type="text"
                className="form-control"
                aria-required="true"
                value={newGroupName}
                onChange={(event) => setNewGroupName(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="new-group-description">
                <i className="pi pi-align-left me-1" aria-hidden="true" />
                {t('admin.groups.descriptionOptional')}
              </label>
              <input
                id="new-group-description"
                type="text"
                className="form-control"
                value={newGroupDescription}
                onChange={(event) => setNewGroupDescription(event.target.value)}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                <i className="pi pi-check me-2" aria-hidden="true" />
                {t('admin.groups.createButton')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <h2>
          <i className="pi pi-users me-2" aria-hidden="true" />
          {t('admin.groups.myGroups')}
        </h2>
        {groups.length === 0 ? (
          <div className="alert alert-info d-flex gap-2 mb-0" role="status">
            <i className="pi pi-info-circle mt-1" aria-hidden="true" />
            <span>{t('admin.groups.noGroup')}</span>
          </div>
        ) : (
          <DataGrid
            rows={groups}
            columns={groupColumns}
            getRowId={(group) => group.id}
            label={t('admin.groups.myGroups')}
            emptyMessage={t('admin.groups.noGroup')}
          />
        )}
      </div>

      {detail && (
        <>
          <div className="card">
            <h2>
              <i className="pi pi-users me-2" aria-hidden="true" />
              {detail.group.name}
            </h2>
            {detail.group.description && <p>{detail.group.description}</p>}
            {!isAdminOfSelected && (
              <div className="alert alert-info d-flex gap-2" role="status">
                <i className="pi pi-lock mt-1" aria-hidden="true" />
                <span>{t('admin.groups.notAdminNotice')}</span>
              </div>
            )}

            <h3>{t('admin.groups.members')}</h3>
            {detail.members.length === 0 ? (
              <p className="hint">{t('admin.groups.noMemberVisible')}</p>
            ) : (
              <DataGrid
                rows={detail.members}
                columns={memberColumns}
                getRowId={(member) => member.userId}
                label={t('admin.groups.members')}
                emptyMessage={t('admin.groups.noMemberVisible')}
              />
            )}

            {isAdminOfSelected && (
              <form onSubmit={handleAddMember} aria-label={t('admin.groups.addMember')} className="mt-3">
                <h3>
                  <i className="pi pi-user-plus me-2" aria-hidden="true" />
                  {t('admin.groups.addMember')}
                </h3>
                <UserPicker selection={memberSelection} onChange={setMemberSelection} disabled={busy} />
                <div className="toolbar">
                  <div className="field">
                    <label htmlFor="member-role">
                      <i className="pi pi-shield me-1" aria-hidden="true" />
                      {t('common.role')}
                    </label>
                    <select
                      id="member-role"
                      className="form-select"
                      value={memberRole}
                      onChange={(event) => setMemberRole(event.target.value as GroupRole)}
                    >
                      {assignableRoles.map((role) => (
                        <option key={role} value={role}>
                          {t(`admin.roles.${role}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary align-self-end"
                    disabled={busy || memberSelection === null}
                  >
                    <i className="pi pi-user-plus me-2" aria-hidden="true" />
                    {t('admin.users.addToGroup')}
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="card">
            <h2>
              <i className="pi pi-share-alt me-2" aria-hidden="true" />
              {t('admin.groups.sharing')}
            </h2>
            <h3>{t('admin.groups.grantsGiven')}</h3>
            {detail.grantsGiven.length === 0 ? (
              <p className="hint">{t('admin.groups.noGrantsGiven')}</p>
            ) : (
              <DataGrid
                rows={detail.grantsGiven}
                columns={grantColumns('given')}
                getRowId={(grant) => grant.id}
                label={t('admin.groups.grantsGiven')}
                emptyMessage={t('admin.groups.noGrantsGiven')}
              />
            )}

            {isAdminOfSelected && (
              <form
                onSubmit={handleCreateGrant}
                aria-label={t('admin.groups.shareWithGroup')}
                className="mt-3"
              >
                <h3>
                  <i className="pi pi-share-alt me-2" aria-hidden="true" />
                  {t('admin.groups.shareWithGroup')}
                </h3>
                <div className="toolbar">
                  <div className="field">
                    <label htmlFor="grant-group">
                      <i className="pi pi-users me-1" aria-hidden="true" />
                      {t('common.group')}
                    </label>
                    <select
                      id="grant-group"
                      className="form-select"
                      value={grantTargetId}
                      onChange={(event) => setGrantTargetId(event.target.value)}
                    >
                      <option value="">{t('admin.users.selectGroup')}</option>
                      {grantCandidates.map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor="grant-permission">
                      <i className="pi pi-key me-1" aria-hidden="true" />
                      {t('common.permission')}
                    </label>
                    <select
                      id="grant-permission"
                      className="form-select"
                      value={grantPermission}
                      onChange={(event) => setGrantPermission(event.target.value as AccessPermission)}
                    >
                      <option value="View">{t('admin.permissions.View')}</option>
                      <option value="Edit">{t('admin.permissions.Edit')}</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary align-self-end"
                    disabled={busy || !grantTargetId}
                  >
                    <i className="pi pi-share-alt me-2" aria-hidden="true" />
                    {t('admin.groups.share')}
                  </button>
                </div>
              </form>
            )}

            <h3>{t('admin.groups.grantsReceived')}</h3>
            {detail.grantsReceived.length === 0 ? (
              <p className="hint">{t('admin.groups.noGrantsReceived')}</p>
            ) : (
              <DataGrid
                rows={detail.grantsReceived}
                columns={grantColumns('received')}
                getRowId={(grant) => grant.id}
                label={t('admin.groups.grantsReceived')}
                emptyMessage={t('admin.groups.noGrantsReceived')}
              />
            )}
          </div>
        </>
      )}
    </>
  );
}
